/**
 * Camada de nível dos rios no mapa.
 *
 * São 154 pontos fixos — três ordens de grandeza abaixo dos focos —, então a
 * camada vetorial comum do OpenLayers dá conta, e o estilo nativo já entra na
 * composição da exportação.
 *
 * O símbolo é um **círculo**: a régua é um ponto fixo no barranco, não tem
 * direção. A seta ficou para as embarcações, que de fato vão para algum lugar.
 * Ao lado dele, um **chevron de texto** (▲ ▼ –) diz se o rio está subindo ou
 * descendo — é a única parte que muda de um ciclo para o outro.
 *
 * O chevron é `Text`, e não parte do sprite, de propósito: se a tendência
 * entrasse na imagem, o cache de estilos passaria de 7 aparências para 21, e
 * com o valor em centímetros junto seria ilimitado.
 *
 * A cada quadro do loop a cota é relida da série de 48 h: é isso que faz o rio
 * subir e descer junto com a animação.
 */
import type { ReactNode } from 'react';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import CircleStyle from 'ol/style/Circle';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import Style from 'ol/style/Style';
import Text from 'ol/style/Text';
import type { FeatureLike } from 'ol/Feature';
import { comSelo } from '../map/contraste';
import { criarCache, dpr, fatorDoZoom } from '../map/escalaDoZoom';
import { Z } from '../map/zIndex';
import { useTimeStore } from '../state/time';
import { ANA_ATRIBUICAO } from './api';
import { COR_TENDENCIA, chaveEstilo, visualDe, type Visual } from './look';
import { SETA_TENDENCIA, cotaNoQuadro } from './nivel';
import { useRiverStore } from './store';
import type { Classe, Rio, Tendencia } from './types';

/** O que o popup recebe ao clicar. */
export interface RioFeature {
  rio: Rio;
  /** Cota no quadro exibido, ou `null` quando o quadro está fora da série. */
  cmNoQuadro: number | null;
  msNoQuadro: number | null;
}

const estilos = criarCache<Style>('rios.estilos', 300);
const chevrons = criarCache<Style>('rios.chevrons', 200);

function estiloCirculo(v: Visual): Style {
  const k = chaveEstilo(v, dpr());
  const pronto = estilos.get(k);
  if (pronto) return pronto;

  const s = new Style({
    image: new CircleStyle({
      radius: v.raio,
      fill: v.preenchimento ? new Fill({ color: v.preenchimento }) : undefined,
      stroke: new Stroke({
        color: v.contorno,
        width: v.larguraContorno,
        lineDash: v.tracejado.length ? [...v.tracejado] : undefined,
      }),
    }),
    // A opacidade entra na cor, não numa propriedade do estilo: `Style` não
    // tem opacidade própria e recriar o círculo por opacidade estouraria o
    // cache.
    zIndex: 0,
  });
  estilos.set(k, s);
  return s;
}

/**
 * Aparência de uma estação na resolução do mapa: o círculo — e, com ele, o
 * chevron e o afastamento — cresce com o zoom pela regra comum
 * (`map/escalaDoZoom.ts`).
 */
export function visualNoZoom(
  classe: Classe | null,
  temCota: boolean,
  estilo: { tamanho: number; opacidade: number },
  resolution: number | undefined,
): Visual {
  return visualDe(classe, temCota, estilo.tamanho * fatorDoZoom(resolution), estilo.opacidade);
}

/** Corpo da letra do chevron, em px, para um círculo de raio `raio`. */
export function fonteDoChevron(raio: number): number {
  return Math.max(9, Math.round(raio * 1.9));
}

function estiloChevron(t: Tendencia, raio: number): Style | null {
  const seta = SETA_TENDENCIA[t];
  if (!seta) return null;
  const k = `${t}|${raio.toFixed(1)}|${dpr().toFixed(2)}`;
  const pronto = chevrons.get(k);
  if (pronto) return pronto;

  const s = new Style({
    text: new Text({
      text: seta,
      font: `600 ${fonteDoChevron(raio)}px ui-sans-serif, system-ui, sans-serif`,
      fill: new Fill({ color: COR_TENDENCIA[t] }),
      stroke: new Stroke({ color: 'rgba(10,16,28,0.75)', width: 2 }),
      offsetX: raio + 5,
      textBaseline: 'middle',
    }),
    zIndex: 1,
  });
  chevrons.set(k, s);
  return s;
}

export interface RiverLayer {
  layer: VectorLayer<VectorSource>;
  dispose: () => void;
}

export function createRiverLayer(renderPopup: (f: RioFeature) => ReactNode | null): RiverLayer {
  const source = new VectorSource({ attributions: ANA_ATRIBUICAO });
  const layer = new VectorLayer({
    source,
    // Acima da máscara do filtro de focos (`AREA_FOCUS`), que apagaria os
    // pontos, e abaixo das estações de chuva e dos focos — queimada continua
    // sendo o assunto principal do portal.
    zIndex: Z.RIVERS,
    className: 'ol-layer amaview-rivers',
    properties: { id: 'rivers', title: 'Nível dos rios (ANA)' },
    updateWhileAnimating: false,
    updateWhileInteracting: false,
  });

  layer.setStyle((feature, resolution) => {
    const d = feature.get('rio') as RioFeature | undefined;
    if (!d) return [];
    const { estilo } = useRiverStore.getState();

    const temCota = d.cmNoQuadro !== null;
    if (!temCota && !estilo.mostrarSemReferencia) return [];
    if (d.rio.classe === null && !estilo.mostrarSemReferencia) return [];

    const v = visualNoZoom(d.rio.classe, temCota, estilo, resolution);
    const saida = [estiloCirculo(v)];

    // O chevron só aparece quando há cota no quadro: dizer que o rio "sobe"
    // num instante sobre o qual não temos medição seria inventar tendência.
    if (estilo.mostrarTendencia && temCota) {
      const c = estiloChevron(d.rio.tendencia, v.raio);
      if (c) saida.push(c);
    }
    // Sobre a foto de satélite do fundo, o selo escuro por baixo (map/contraste.ts).
    return comSelo(saida, v.raio + v.larguraContorno / 2 + 3, resolution);
  });

  layer.set('popup', (feature: FeatureLike): ReactNode | null => {
    const d = feature.get('rio') as RioFeature | undefined;
    return d ? renderPopup(d) : null;
  });

  let codigosDesenhados: readonly string[] = [];

  function reconstruir(): void {
    const { dados } = useRiverStore.getState();
    source.clear(true);
    source.addFeatures(
      dados.rios.map((r) => {
        const f = new Feature({ geometry: new Point([r.estacao.mx, r.estacao.my]) });
        f.set('rio', { rio: r, cmNoQuadro: r.cm, msNoQuadro: r.ms } satisfies RioFeature);
        return f;
      }),
    );
    codigosDesenhados = dados.rios.map((r) => r.estacao.cod);
    reavaliar();
  }

  /**
   * Relê a cota de cada estação na hora do quadro exibido.
   *
   * Só redesenha quando alguma estação de fato muda, senão o loop mandaria a
   * camada se repintar cinco vezes por segundo sem nada novo.
   */
  function reavaliar(): void {
    const { dados, visivel } = useRiverStore.getState();
    layer.setVisible(visivel);
    if (!visivel) return;

    const { displayedTime, currentTime } = useTimeStore.getState();
    const instante = displayedTime ?? currentTime;

    let mudou = false;
    for (const f of source.getFeatures()) {
      const d = f.get('rio') as RioFeature;
      const achado = cotaNoQuadro(d.rio.serie, dados.t0, dados.passoMin, instante);
      const cm = achado?.cm ?? null;
      const ms = achado?.ms ?? null;
      if (cm === d.cmNoQuadro && ms === d.msNoQuadro) continue;
      f.set('rio', { rio: d.rio, cmNoQuadro: cm, msNoQuadro: ms } satisfies RioFeature, true);
      mudou = true;
    }
    if (mudou) layer.changed();
  }

  const pararStore = useRiverStore.subscribe((a, b) => {
    if (a.dados !== b.dados) {
      const novos = a.dados.rios.map((r) => r.estacao.cod);
      const mudou = novos.length !== codigosDesenhados.length || novos.some((c, i) => c !== codigosDesenhados[i]);
      if (mudou) reconstruir();
      else {
        // Mesma lista, dados novos: cada feature precisa do `rio` NOVO (série,
        // cota, classe), senão a série da hora passada é lida com o `t0` de
        // agora e o rio aparece deslocado uma hora — e a ficha mostra a
        // classe velha. Era o que acontecia após a primeira revalidação.
        const porCod = new Map(a.dados.rios.map((r) => [r.estacao.cod, r]));
        for (const f of source.getFeatures()) {
          const d = f.get('rio') as RioFeature;
          const novo = porCod.get(d.rio.estacao.cod);
          if (novo) f.set('rio', { rio: novo, cmNoQuadro: null, msNoQuadro: null } satisfies RioFeature, true);
        }
        reavaliar();
        layer.changed();
      }
      return;
    }
    if (a.visivel !== b.visivel) reavaliar();
    else if (a.estilo !== b.estilo) layer.changed();
  });

  const pararTempo = useTimeStore.subscribe((a, b) => {
    if (a.displayedTime !== b.displayedTime || a.currentTime !== b.currentTime) reavaliar();
  });

  reconstruir();

  return {
    layer,
    dispose: () => {
      pararStore();
      pararTempo();
      source.clear(true);
    },
  };
}
